import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Input,
  Icon,
} from "@elaniintc/goes-design-system";
import { VariableSelector } from "./VariableSelector";
import type { NodeVariable } from "../types";

interface ParameterInputProps {
  param: {
    display_name: string;
    description: string;
    name: string;
    type: string;
    example?: string;
    optional?: boolean;
    icon?: string;
  };
  value: string;
  onChange: (value: string) => void;
  variables?: NodeVariable[];
}

const getInputType = (type: string): string => {
  const lowerType = type.toLowerCase();
  if (
    lowerType === "integer" ||
    lowerType === "int" ||
    lowerType === "number" ||
    lowerType === "float" ||
    lowerType === "double" ||
    lowerType === "decimal" ||
    lowerType === "long" ||
    lowerType === "short"
  )
    return "number";
  if (lowerType === "date") return "date";
  if (lowerType === "email") return "email";
  return "text";
};

const getVariableType = (type: string): "string" | "number" => {
  const lowerType = type.toLowerCase();
  if (
    lowerType === "integer" ||
    lowerType === "int" ||
    lowerType === "number" ||
    lowerType === "float" ||
    lowerType === "double" ||
    lowerType === "decimal" ||
    lowerType === "long" ||
    lowerType === "short"
  )
    return "number";
  return "string";
};

const mapParamTypeToVariableType = (
  type: string,
): "string" | "number" | "date" | "boolean" => {
  const lowerType = type.toLowerCase();
  if (
    lowerType === "integer" ||
    lowerType === "int" ||
    lowerType === "number" ||
    lowerType === "float" ||
    lowerType === "double" ||
    lowerType === "decimal" ||
    lowerType === "long" ||
    lowerType === "short"
  )
    return "number";
  if (lowerType === "date") return "date";
  if (lowerType === "boolean" || lowerType === "bool") return "boolean";
  return "string";
};

export const ParameterInput: React.FC<ParameterInputProps> = ({
  param,
  value,
  onChange,
  variables = [],
}) => {
  const { display_name, icon, type, example, optional } = param;
  const [inputType, setInputType] = useState<"input" | "variable">("input");
  const [inputValue, setInputValue] = useState<string>("");
  const [variableValue, setVariableValue] = useState<string>("");
  const isInternalUpdate = useRef(false);

  const strictVariableType = mapParamTypeToVariableType(type);
  const compatibleVariables = React.useMemo(() => {
    return (variables || [])
      .map((node) => ({
        ...node,
        variables: (node.variables || []).filter(
          (v) => v.type === strictVariableType,
        ),
      }))
      .filter((node) => node.variables.length > 0);
  }, [variables, strictVariableType]);

  // Initialize values from prop when value changes externally
  useEffect(() => {
    // Skip if this is our own update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (value) {
      // Check if value is a variable name
      const isVariableByName = variables.some((node) =>
        node.variables.some((v) => v.name === value),
      );
      // Also check if value is a variable ID (for backward compatibility)
      const isVariableById = variables.some((node) =>
        node.variables.some((v) => v.id === value),
      );
      const isVariable = isVariableByName || isVariableById;

      if (isVariable) {
        if (inputType !== "variable") {
          setInputType("variable");
        }
        // If it's an ID, try to find the name; otherwise use the value as-is
        let variableNameToUse = value;
        if (isVariableById && !isVariableByName) {
          // It's an ID, find the name
          for (const node of variables) {
            const variable = node.variables.find((v) => v.id === value);
            if (variable) {
              variableNameToUse = variable.name;
              break;
            }
          }
        }
        setVariableValue(variableNameToUse);
        setInputValue("");
      } else {
        if (inputType !== "input") {
          setInputType("input");
        }
        setInputValue(value);
        setVariableValue("");
      }
    } else {
      // Clear both if value is empty
      setInputValue("");
      setVariableValue("");
    }
  }, [value, variables, inputType]);

  const handleToggleType = () => {
    const newType = inputType === "input" ? "variable" : "input";
    setInputType(newType);
    isInternalUpdate.current = true;
    // Clear the opposite value
    if (newType === "input") {
      setVariableValue("");
      onChange(inputValue);
    } else {
      setInputValue("");
      onChange(variableValue);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <label className="text-sm font-medium text-body-loud">
          {display_name}
        </label>
        {!optional && <span className="text-danger">*</span>}
      </div>
      <GroupTextField label="" className="flex-1">
        <GroupTextFieldRow className="overflow-x-hidden">
          <Button variant="quiet" group="up-left" onClick={handleToggleType}>
            {inputType === "input" ? "inp" : "var"}
          </Button>
          {inputType === "input" ? (
            <div className="relative w-full">
              {icon && (
                <Icon
                  name={icon}
                  type="fas"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-body"
                />
              )}
              <Input
                className={`w-full ${icon ? "pl-9" : "pl-9"}`}
                placeholder={example || "Ingrese valor..."}
                type={getInputType(type)}
                group="up-right"
                value={inputValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setInputValue(newValue);
                  isInternalUpdate.current = true;
                  onChange(newValue);
                }}
              />
            </div>
          ) : (
            <VariableSelector
              variables={compatibleVariables}
              type={getVariableType(type)}
              group="up-right"
              selectedValue={variableValue}
              onUpdateElement={(value) => {
                setVariableValue(value);
                isInternalUpdate.current = true;
                onChange(value);
              }}
              placeholder="Seleccionar variable"
              useNameAsValue={true}
            />
          )}
        </GroupTextFieldRow>
      </GroupTextField>
    </div>
  );
};
